// ============================================================
// First 90 Days — touchpoint definitions
//
// Five moments in a client's first 90 days. The two early ones are short
// nudges rather than meetings; they exist because clients have left in weeks
// two and three, before any 30-day call would have reached them.
// ============================================================

export const TOUCHPOINTS = [
  {
    kind: 'week1_welcome',
    day: 7,
    label: 'Week 1 — Welcome note',
    owner: 'Tori',
    short: 'Send',
    purpose: 'First contact after kickoff. Warm, short, no ask.',
    checklist: [
      'Tell them the team is underway and enjoying the work',
      'Restate the goals and timeline agreed at kickoff, including anything that changed on the call — everyone should now be working from the same set of numbers and dates',
      'Name one specific thing already in progress',
      'Restate when the first visible thing lands',
      'No questions, no requests — this is reassurance, not a status form',
    ],
    // A starting point, not a script. Placeholders fill from the client record.
    template: `Hi {first},

Quick note now that we're a week in — the team has started on {client} and they're enjoying it.

So we're all working from the same page coming out of kickoff, here's what we're aiming at: {goals_recap}. The timeline we're holding ourselves to: {timeline_recap}. {kickoff_changes}

Right now we're working on {in_progress}. The first thing you'll actually see is {first_visible}, around {first_visible_when}.

Nothing needed from you. I'll check in properly in a few weeks, but reach out any time before then.

{sender}`,
  },
  {
    kind: 'week2_check',
    day: 14,
    label: 'Week 2 — Delivery check',
    owner: 'Lauren',
    short: 'Check',
    purpose: 'Internal only. Has the work actually started?',
    internal: true,
    checklist: [
      'At least one task completed or genuinely in progress per service',
      'Nothing blocked waiting on access, logins, or assets',
      'Info Grab returned and usable',
      'If anything is stalled, unblock it now rather than explaining it at day 30',
    ],
  },
  {
    kind: 'day30',
    day: 30,
    label: 'Day 30 — Progress check',
    owner: 'Tori',
    short: 'Call',
    purpose: 'Restate the goals and timeline, show what has been done, and say what comes next.',
    checklist: [
      'Restate their goals in their own words from the kickoff',
      'Walk the timeline — where we are against what we said',
      'Show what has been delivered in the first 30 days',
      'Say what is coming in the next 30, with dates',
      'Re-anchor when results become visible for their service mix',
      'Ask how they are finding it so far — open question, then stop talking',
    ],
    escalate: [
      'They cannot state the goal → the kickoff did not land, redo it',
      'They expected something we are not doing → resolve this week, in writing',
      'We are behind the timeline → give a new date before they ask for one',
      'They only ever wanted setup → reclassify as a project now, not at day 90',
    ],
  },
  {
    kind: 'day60',
    day: 60,
    label: 'Day 60 — Value check',
    owner: 'Tori',
    short: 'Call',
    purpose:
      'Make the case for the investment before they start making it privately. ' +
      'Show the work, the progress, and the forecast — do not ask them to justify it.',
    checklist: [
      'Everything delivered in 60 days, laid out plainly',
      'Progress against each goal, including where it is behind',
      'The ROI forecast from their proposal, and where we are against it',
      'Timeline: what lands in the next 30 days',
      'Any early signal, however small — leading indicators count',
      'Ask how they think it is going',
      'Ask whether anything has changed in their business that shifts the goal',
    ],
    escalate: [
      'They cannot name anything they have received → a reporting failure, fix visibility now',
      'Goals flat with no explanation they have heard → say it first, before they raise it',
      'They mention budget, a slow season, or an accountant → offer a scope option before they ask to leave',
      'The forecast is now unreachable → reset it honestly rather than letting it quietly expire',
      'Their business has changed → rewrite the goal now, do not keep measuring the old one',
    ],
  },
  {
    kind: 'day90',
    day: 90,
    label: 'Day 90 — Goals reset',
    owner: 'Tori + AD',
    short: 'Review',
    purpose:
      'Measure against the goal, set the health status, and decide whether this client is ' +
      'ready for normal AD cadence — or whether we keep watching.',
    setsHealth: true,
    checklist: [
      'Restate the kickoff goal and show progress honestly',
      'Confirm, revise, or replace the goal for months 4–6',
      'Ask how they feel about the first three months',
      'Confirm scope and spend for next quarter',
      'Set the health status',
      'Decide: hand off to the AD, or keep monitoring',
    ],
    escalate: [
      'Goals not met → not a handoff. Lauren and Tori keep watching through months 4-6',
      'Unhappy or hesitant → not a handoff, whatever the numbers say',
      'Goals met and they are happy → hand to the AD with a written baseline',
      'Never really a retainer client → reclassify now rather than counting them as churn later',
    ],
  },
]

// Months 4-6. Lighter: one check a month, AD-owned, Tori reviewing.
export const ESTABLISHING_TOUCHPOINTS = [
  { kind: 'month4', day: 120, label: 'Month 4 — Progress check', owner: 'AD', short: 'Check',
    purpose: 'First month past onboarding. Is the goal still the goal?',
    checklist: [
      'Record where the goal metric stands',
      'Confirm the client still considers it the right goal',
      'Anything they have stopped mentioning that they used to ask about',
    ] },
  { kind: 'month5', day: 150, label: 'Month 5 — Results check', owner: 'AD', short: 'Check',
    purpose: 'By now the outcome metric should be moving on most service lines.',
    checklist: [
      'Record the goal metric',
      'If it has not moved, say so before they raise it',
      'Flag to Tori if two months have passed with no movement',
    ] },
  { kind: 'month6', day: 180, label: 'Month 6 — Goal verdict', owner: 'Tori + AD', short: 'Review',
    purpose: 'The goal had a target date. Did it land?',
    setsHealth: true,
    checklist: [
      'Measure against the original target',
      'Set the next goal, or agree there is not one',
      'Reset health status',
      'Graduate to normal AD cadence, or keep them in view',
    ] },
]

export const ALL_TOUCHPOINTS = [...TOUCHPOINTS, ...ESTABLISHING_TOUCHPOINTS]

export const PHASES = {
  onboarding:   { label: 'Onboarding',   days: 'Days 1-90',    color: 'var(--gold)',       next: 'establishing' },
  establishing: { label: 'Establishing', days: 'Months 4-6',   color: 'var(--teal)',       next: 'graduated' },
  graduated:    { label: 'Graduated',    days: 'Normal cadence', color: 'var(--text-muted)', next: null },
  paused:       { label: 'Paused',       days: 'On hold',      color: 'var(--text-muted)', next: null },
}

export const GOAL_TYPES = {
  number:    { label: 'A number to move', hint: 'Measured over time — inquiries, bookings, rankings.' },
  milestone: { label: 'A thing to achieve', hint: 'Done or not done. No numbers.' },
}

export const NOTE_SOURCES = {
  client:     { label: 'Client said', color: 'var(--navy)' },
  ad:         { label: 'AD read',     color: 'var(--teal)' },
  touchpoint: { label: 'Touchpoint',  color: 'var(--gold)' },
  internal:   { label: 'Internal',    color: 'var(--text-muted)' },
}

export const GOAL_STATUS = {
  active:  { label: 'Active',  color: 'var(--navy)' },
  met:     { label: 'Met',     color: 'var(--green)' },
  missed:  { label: 'Missed',  color: 'var(--red)' },
  revised: { label: 'Revised', color: '#c4962a' },
  dropped: { label: 'Dropped', color: 'var(--text-muted)' },
}

export const COMMITMENT_STATUS = {
  open:          { label: 'Open',          color: 'var(--navy)' },
  met:           { label: 'Met',           color: 'var(--green)' },
  missed:        { label: 'Missed',        color: 'var(--red)' },
  renegotiated:  { label: 'Renegotiated',  color: '#c4962a' },
  cancelled:     { label: 'Cancelled',     color: 'var(--text-muted)' },
}

export const COMMITMENT_SOURCE = {
  sales_call: 'Sales call', kickoff: 'Kickoff', touchpoint: 'Touchpoint',
  email: 'Email', meeting: 'Meeting', contract: 'Contract',
}

// Hit rate on promises. The single most concrete measure of whether a client
// has reason to trust the next date you give them.
export const commitmentStats = (rows = []) => {
  const closed = rows.filter(r => r.status === 'met' || r.status === 'missed')
  const met = closed.filter(r => r.status === 'met').length
  const open = rows.filter(r => r.status === 'open')
  const overdue = open.filter(r => new Date(r.due_date + 'T12:00:00') < new Date())
  // Met but never told about — delivered silently earns nothing.
  const silent = rows.filter(r => r.status === 'met' && !r.client_told)
  return {
    total: rows.length, met, missed: closed.length - met, open: open.length,
    overdue: overdue.length, silent: silent.length,
    hitRate: closed.length ? (met / closed.length) * 100 : null,
  }
}

// Progress toward a goal target, allowing for goals that count down as well as up.
// Progress toward a target. With no baseline recorded we measure from zero,
// which is the honest reading of "we don't know where they started".
export const goalProgress = (goal, latestValue) => {
  if (goal?.goal_type === 'milestone') return null
  if (goal?.target == null || latestValue == null) return null
  const base = goal.baseline ?? 0
  const span = goal.target - base
  if (span === 0) return null
  return Math.max(0, Math.min(100, ((latestValue - base) / span) * 100))
}

// Days until a target date. Negative means past due.
export const daysUntil = (d) => {
  if (!d) return null
  const t = new Date(String(d).slice(0, 10) + 'T12:00:00')
  if (isNaN(t.getTime())) return null
  return Math.ceil((t.getTime() - Date.now()) / 86400000)
}

// ── Client flags ────────────────────────────────────────────────────────────
// Multi-select. Grouped by what they imply, because the group determines the
// play: a delivery problem and a perception problem look identical from the
// outside and need opposite responses.
//
// `then` is the pre-decided action. Deciding what to do while looking at a bad
// number is how everything gets rationalised away.
export const FLAG_GROUPS = {
  good:       { label: 'Going well',        color: 'var(--green)',      bg: 'var(--lightgreen-bg)' },
  delivery:   { label: 'Delivery',          color: '#c4962a',           bg: '#fff8e1' },
  perception: { label: 'How they see us',   color: 'var(--mauve)',      bg: 'var(--blush)' },
  outcome:    { label: 'Results',           color: 'var(--navy)',       bg: '#eef2fa' },
  risk:       { label: 'Risk',              color: 'var(--red)',        bg: 'var(--red-bg)' },
}

export const FLAGS = [
  // Going well
  { key: 'on_track',      group: 'good',       label: 'On track',
    then: 'Keep the cadence. Nothing needed.' },
  { key: 'delighted',     group: 'good',       label: 'Genuinely pleased',
    then: 'Ask for a testimonial or referral while it is true.' },
  { key: 'growth',        group: 'good',       label: 'Room to grow the account',
    then: 'Raise scope at the next touchpoint, not before.' },

  // Delivery
  { key: 'on_schedule',   group: 'delivery',   label: 'On schedule',
    then: 'Say so to the client. Being on time is invisible unless you mention it.' },
  { key: 'ahead',         group: 'delivery',   label: 'Ahead of schedule',
    then: 'Bank the goodwill — tell them before they notice, or they never will.' },
  { key: 'overdue_work',  group: 'delivery',   label: 'Overdue deliverables',
    then: 'Fix the backlog before the next client call. Do not explain, deliver.' },
  { key: 'blocked_on_us', group: 'delivery',   label: 'Blocked on us',
    then: 'Unblock this week — it compounds into a missed commitment.' },
  { key: 'blocked_on_them', group: 'delivery', label: 'Waiting on the client',
    then: 'Chase directly, and stop counting the delay against the team.' },
  { key: 'missed_promise', group: 'delivery',  label: 'We missed a promised date',
    then: 'Acknowledge it before they raise it. Give a new date and hit it.' },

  // How they see us
  { key: 'engaged',       group: 'perception',  label: 'Engaged and responsive',
    then: 'Good time to ask what else they need. Engaged clients say yes.' },
  { key: 'sees_value',    group: 'perception',  label: 'Sees the value clearly',
    then: 'Ask for the testimonial or referral now, while it is true.' },
  { key: 'cant_see_value', group: 'perception', label: "Can't see what they're getting",
    then: 'A reporting problem, not a work problem. Send what has been delivered.' },
  { key: 'questioning_cost', group: 'perception', label: 'Questioning the cost',
    then: 'Value conversation now, with the scope menu ready. Do not wait.' },
  { key: 'unhappy',       group: 'perception',  label: 'Unhappy or frustrated',
    then: 'Call, do not email. Find the specific thing before it becomes general.' },
  { key: 'gone_quiet',    group: 'perception',  label: 'Gone quiet',
    then: 'Silence is not neutral. Call this week.' },
  { key: 'contact_changed', group: 'perception', label: 'Main contact changed',
    then: 'Re-run the kickoff conversation. The new person bought nothing.' },

  // Results
  { key: 'goals_moving',  group: 'outcome',    label: 'Goals moving',
    then: 'Show them. Movement they do not know about is worth nothing.' },
  { key: 'goals_hit',     group: 'outcome',    label: 'Goal hit',
    then: 'Mark it met, then set the next one. A finished goal with no successor drifts.' },
  { key: 'goals_flat',    group: 'outcome',    label: 'Goals not moving',
    then: 'Say it first. If it is too early to expect movement, say that instead.' },
  { key: 'goals_shifted', group: 'outcome',    label: 'Their goals have shifted',
    then: 'Rewrite the goal now. Measuring the old one is worse than useless.' },
  { key: 'too_early',     group: 'outcome',    label: 'Too early to judge results',
    then: 'Re-anchor the timeline in writing. This is where month-2 churn starts.' },

  // Risk
  { key: 'no_concerns',   group: 'risk',       label: 'No concerns',
    then: 'Nothing to action. Worth recording — it dates the last clean read.' },
  { key: 'renewal_likely', group: 'risk',      label: 'Would renew today',
    then: 'The strongest signal there is. Note what is working so it can be repeated.' },
  { key: 'at_risk',       group: 'risk',       label: 'At risk of leaving',
    then: 'Intervention plan with a date this week. Tori joins the next call.' },
  { key: 'paying_late',   group: 'risk',       label: 'Paying late',
    then: 'The strongest financial-churn signal there is. Raise it early and kindly.' },
  { key: 'scope_creep',   group: 'risk',       label: 'Asking for more than we sold',
    then: 'Reset the scope in writing before resentment sets in on either side.' },
  { key: 'not_retainer',  group: 'risk',       label: 'May not be a retainer client',
    then: 'Decide by day 90. Reclassify rather than counting them as churn later.' },
]

export const getFlag = k => FLAGS.find(f => f.key === k)

// These come from the monthly AD check-in, not from Tori talking to clients.
// The AD reports; Tori and the AD set the flags together. Anything older than
// a cycle is stale by definition.
export const REVIEW_CADENCE_DAYS = 30

export const reviewAge = (dateStr) => {
  if (!dateStr) return null
  const d = new Date(String(dateStr).slice(0, 10) + 'T12:00:00')
  if (isNaN(d.getTime())) return null
  return Math.floor((Date.now() - d.getTime()) / 86400000)
}

// What the AD is asked at the check-in. Keeps the conversation to the things
// that actually predict churn rather than a general "how are they doing".
export const REVIEW_PROMPTS = [
  { group: 'outcome',    ask: 'Are their goals moving? If not, is it too early to expect it?' },
  { group: 'delivery',   ask: 'Are we hitting the dates we set? Anything they are waiting on?' },
  { group: 'perception', ask: 'What is their read on us right now — and how do you know?' },
  { group: 'risk',       ask: 'If they had to renew today, would they?' },
]

// The flags that should pull a client onto Tori's agenda.
export const URGENT_FLAGS = ['at_risk', 'unhappy', 'questioning_cost', 'gone_quiet',
  'missed_promise', 'paying_late', 'contact_changed']

export const flagSummary = (flags = []) => {
  const urgent = flags.filter(f => URGENT_FLAGS.includes(f))
  const groups = {}
  flags.forEach(f => {
    const g = getFlag(f)?.group
    if (g) groups[g] = (groups[g] || 0) + 1
  })
  return { urgent, groups, count: flags.length }
}

export const RESPONSIVENESS = {
  quick:  { label: 'Quick',  color: 'var(--green)',      note: 'Replies within a day' },
  normal: { label: 'Normal', color: 'var(--text-muted)', note: 'Replies within a few days' },
  slow:   { label: 'Slow',   color: '#c4962a',           note: 'Chasing for replies' },
  silent: { label: 'Silent', color: 'var(--red)',        note: 'Not responding — the strongest churn signal there is' },
}

// Days since we last heard from them. Long gaps precede terminations more
// reliably than anything in the task data.
export const daysSinceContact = (d) => {
  if (!d) return null
  const t = new Date(String(d).slice(0, 10) + 'T12:00:00')
  if (isNaN(t.getTime())) return null
  return Math.floor((Date.now() - t.getTime()) / 86400000)
}

export const getTouchpoint = kind => ALL_TOUCHPOINTS.find(t => t.kind === kind)

export const HEALTH = {
  healthy:       { label: 'Healthy',       color: 'var(--green)',      bg: 'var(--lightgreen-bg)', note: 'On track and engaged' },
  watch:         { label: 'Watch',         color: '#c4962a',           bg: '#fff8e1',              note: 'Behind, or gone quiet' },
  at_risk:       { label: 'At risk',       color: 'var(--red)',        bg: 'var(--red-bg)',        note: 'Value questioned or goal unreachable' },
  not_retainer:  { label: 'Not a retainer',color: 'var(--text-muted)', bg: 'var(--cream)',         note: 'Project work — exclude from churn' },
}

// Day number since start. Day 1 is the start date itself.
export const dayNumber = (startDate) => {
  if (!startDate) return null
  const s = new Date(String(startDate).slice(0, 10) + 'T12:00:00')
  if (isNaN(s.getTime())) return null
  return Math.floor((Date.now() - s.getTime()) / 86400000) + 1
}

// A touchpoint's state drives every color and sort order in the UI.
export const touchpointState = (tp) => {
  if (!tp) return 'none'
  if (tp.skipped) return 'skipped'
  if (tp.completed_at) return 'done'
  const due = new Date(String(tp.due_date).slice(0, 10) + 'T12:00:00')
  const days = Math.floor((due.getTime() - Date.now()) / 86400000)
  if (days < 0) return 'overdue'
  if (days <= 3) return 'due'
  return 'upcoming'
}

export const STATE_STYLE = {
  overdue:  { color: 'var(--red)',        bg: 'var(--red-bg)',        label: 'Overdue' },
  due:      { color: '#c4962a',           bg: '#fff8e1',              label: 'Due now' },
  upcoming: { color: 'var(--text-muted)', bg: 'transparent',          label: 'Upcoming' },
  done:     { color: 'var(--green)',      bg: 'var(--lightgreen-bg)', label: 'Done' },
  skipped:  { color: 'var(--text-muted)', bg: 'var(--cream)',         label: 'Skipped' },
  none:     { color: 'var(--text-muted)', bg: 'transparent',          label: '—' },
}

// Fills the welcome-note template. Anything unknown stays as a visible
// [PLACEHOLDER] rather than silently vanishing.
export const fillTemplate = (tpl, vals) =>
  tpl.replace(/\{(\w+)\}/g, (_, k) => vals[k] || `[${k.toUpperCase()}]`)